import { useMemo, useRef, useState } from 'react'
import { Text } from '@/components/commons/typography/Text'
import { Title } from '@/components/commons/typography/Title'
import { Avatar } from 'containers/recommendations/Avatar'

import { User } from 'containers/user/types/user'
import Link from 'next/link'
import {
  buttonsContainer,
  buttonsContainerSmall,
  profileCardSmall,
  profileInfoContainer,
  profileInfoContainerSmall,
  textContainer,
} from '../../styles/connectionCard.css'
import { cssClass } from '@/shared/utils'
import {
  getProfileLink,
  getProfileSubtitleCardCopy,
} from 'containers/profile/utilities/profile'
import { ProfileRecommendation } from 'containers/recommendations/types/recommendations'
import { MutualConnections } from '../mutual-connections/MutualConnections'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  arrow,
  FloatingArrow,
  useTransitionStyles,
} from '@floating-ui/react'
import { colorsV2 } from '@/styles/v2/vars/vars.colors'
import { DisplayNameWithBadge } from '@/components/user/DisplayNameWithBadge'
import { useIsMod, useSubscription } from 'containers/profile/hooks/useProfile'
import { Badge } from '@mantine/core'

interface ProfileCardProps {
  profile: User | ProfileRecommendation
  useSmallCard?: boolean
  children?: React.ReactNode
  isInRecommendationsColumn?: boolean
  useToolTip?: boolean
  showIsHiringBadge?: boolean
  showStaffBadge?: boolean
  showMutualConnections?: boolean
}

export const ProfileCardBase = ({
  profile,
  useSmallCard,
  children,
  isInRecommendationsColumn = false,
  useToolTip = true,
  showIsHiringBadge,
  showStaffBadge = true,
  showMutualConnections = true,
}: ProfileCardProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const arrowRef = useRef(null)
  const isMod = useIsMod(profile)
  const { isSubscribed } = useSubscription(profile)

  const { refs, floatingStyles, context, placement } = useFloating({
    placement: 'top',
    whileElementsMounted: autoUpdate,
    open: isHovered,
    onOpenChange: setIsHovered,
    // order matters here
    middleware: [offset(10), flip(), shift(), arrow({ element: arrowRef })],
  })

  const { isMounted, styles } = useTransitionStyles(context)

  const hover = useHover(context, {
    move: false,
    mouseOnly: true,
  })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, {
    role: 'tooltip',
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ])

  // update border styles based on where the arrow is pointing
  const borderClass = useMemo(() => {
    switch (placement) {
      case 'bottom':
        return 'border-x-1 border-t-0 border-b-1'
      case 'left':
        return 'border-y-1 border-l-0 border-r-1'
      case 'right':
        return 'border-y-1 border-r-0 border-l-1'
      default:
        return 'border-x-1 border-t-1 border-b-0'
    }
  }, [placement])

  return (
    <>
      <div
        className={cssClass([
          'full-w bg-white-primary p-16 rounded-16',
          useSmallCard && !isInRecommendationsColumn && profileCardSmall,
        ])}
      >
        <div
          className={cssClass([
            'flex flex-column gap-20 justify-space-between items-center',
            !useSmallCard && 'flex-row--tablet gap-5--tablet',
          ])}
        >
          <div
            className={cssClass([
              'flex items-center gap-12 full-w flex-1',
              profileInfoContainer,
              useSmallCard && profileInfoContainerSmall,
            ])}
          >
            <div ref={refs.setReference} {...getReferenceProps()}>
              <Avatar
                image={profile?.avatar}
                alt={`${profile?.username}_connection_avatar`}
                size={48}
                to={profile?.username && getProfileLink(profile)}
              />
            </div>

            <div
              className={cssClass(['flex flex-column gap-4', textContainer])}
            >
              {showIsHiringBadge && profile?.isHiring && (
                <Badge
                  color="black"
                  size="xs"
                  className="text-11 text-accent-primary"
                  py={0}
                  radius={8}
                >
                  🤝 Hiring
                </Badge>
              )}
              <LinkContainer profile={profile}>
                <Title
                  as={'h2'}
                  sizeClass="text-15"
                  weightClass="font-bold"
                  className="break-text"
                  lineHeightClass="leading-20"
                >
                  <DisplayNameWithBadge
                    profile={profile}
                    hideBadge={!showStaffBadge}
                  />{' '}
                  <Title
                    as="span"
                    sizeClass="text-14"
                    weightClass="font-normal"
                    colorClass="text-tertirary"
                    lineHeightClass="leading-20"
                  >
                    {!isMod && !isSubscribed && profile?.username}
                  </Title>
                </Title>
              </LinkContainer>

              <Text
                sizeClass="text-14"
                lineHeightClass="leading-20"
                className="break-text"
              >
                {getProfileSubtitleCardCopy(profile)}
              </Text>

              {showMutualConnections && (
                <MutualConnections profile={profile} showAvatars={false} />
              )}
            </div>
          </div>
          {children && (
            <div
              className={cssClass([
                buttonsContainer,
                useSmallCard && buttonsContainerSmall,
              ])}
            >
              {children}
            </div>
          )}
        </div>
      </div>

      {useToolTip && isMounted && (
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, ...styles }}
          className={cssClass([
            'bg-tertiary border-solid border-secondary p-12 rounded-20 z-10',
            borderClass,
          ])}
          {...getFloatingProps()}
        >
          <FloatingArrow
            ref={arrowRef}
            context={context}
            fill={colorsV2.background.light.tertiary}
          />
          <Avatar
            image={profile?.avatar}
            alt={`${profile?.username}_connection_avatar`}
            size={120}
          />
        </div>
      )}
    </>
  )
}

export const LinkContainer = ({
  children,
  profile,
}: {
  children: React.ReactNode
  profile: User
}) => {
  if (!profile?.username) return <>{children}</>

  return (
    <Link href={getProfileLink(profile)} className="hover__text-underline">
      {children}
    </Link>
  )
}
